// @givmo/app-admin — the L4 flip ceremony (AS-WEB6).
//
// The one screen in this product that can stop a person signing in to their own account. Choosing
// `signin_lock` does not set a form value here — it opens THIS, and the tier write happens only from
// inside it.
//
// ══ THE EXIT BAR ══════════════════════════════════════════════════════════════════════════════
// *An Owner cannot reach L4 without seeing and acknowledging the warnings.* Three gates, all of
// which must hold before Confirm is enabled, and every one of them fails CLOSED:
//
//   1. THE PRE-FLIGHT COUNTS HAVE ACTUALLY ARRIVED — `countsComplete`, not `data !== undefined`.
//      A failed read leaves the Owner with a retry and nothing else; so does a read that SUCCEEDED
//      and carried no numbers, because the tolerant decode turns a `{}` body into a perfectly
//      defined all-`null` object and "the request returned" is not the property this gate is about.
//      It is the gate most likely to be argued with ("let them proceed, the server is
//      authoritative"), and the answer is that the server would happily lock a workforce the Owner
//      never got to count. The whole point of the panel is that the number is seen BEFORE the click.
//      A REFETCH IN FLIGHT counts as not-yet-arrived too: the observer outlives a close, so a
//      reopened ceremony would otherwise gate on the previous opening's numbers.
//   2. THE ACKNOWLEDGEMENT IS TICKED — an explicit act against the specific sentence about
//      employees who cannot use the identity provider, not a general "I agree".
//   3. THE COMPANY NAME IS TYPED. Deliberateness, not authorisation; the backend's Owner check is
//      the authorisation. `confirmationMatches` is exact on the name and tolerant on the typing —
//      see its own note for why byte-exact capitalisation would make this worse, not stricter.
//
// ══ WHAT THE PANEL MAY AND MAY NOT SAY ════════════════════════════════════════════════════════
// COUNTS, NEVER A ROSTER — the backend returns no names, and this adds none. A count that did not
// arrive renders as nothing at all rather than as `0`: "this affects nobody" is the single most
// dangerous sentence this screen could invent, and `sso-decode.ts` makes it unspellable by decoding
// every count tri-state.
//
// Two lines exist because a NUMBER alone would mislead:
//   • `min_client_version: null` means the app-version check DID NOT RUN, so the two version counts
//     are zero for a reason that has nothing to do with anyone's build.
//   • `exempt_admins === 0` needs saying because the count is narrower than it looks: it counts only
//     admins who are ALSO linked employees. A zero does NOT mean nobody can turn the tier off — an
//     admin who is not a linked employee is never locked at all — so it renders as a NEUTRAL note,
//     not a warning. See `hasNoExemptAdmin` for the roster walk this rests on.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Checkbox, Dialog, Field, Skeleton, testIdProp } from '@givmo/design-system';
import { SSO_COPY_DRAFT } from './security-copy';
import {
  clientFloorWasChecked,
  confirmationMatches,
  countsComplete,
  hasNoExemptAdmin,
  readLockReadiness as decodeLockReadiness,
  type LockReadiness,
} from './sso-decode';
import { readLockReadiness } from './sso-config-transport';

export const LOCK_CEREMONY_TEST_ID = 'settings-security-sso-lock';

/** Its own query key — a one-shot pre-flight read, never pooled with the section's config read. */
export const LOCK_READINESS_QUERY_KEY = ['admin-company-sso-lock-readiness'] as const;

export interface SsoLockCeremonyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the Owner must type. The company's own name, from the settings page's company read. */
  companyName: string;
  /** Fires only once all three gates hold. The caller owns the `PUT …/policy`. */
  onConfirm: () => void;
  submitting: boolean;
  /** A failed save, rendered inside the dialog so the ceremony is not dismissed by its own error. */
  errorMessage?: string | null;
}

export function SsoLockCeremonyDialog({
  open,
  onOpenChange,
  companyName,
  onConfirm,
  submitting,
  errorMessage,
}: SsoLockCeremonyDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [typed, setTyped] = useState('');

  // Reset on a genuine CLOSED→OPEN transition only, so a reopened ceremony never arrives
  // pre-acknowledged from the last time it was cancelled. An Owner passes all three gates every
  // time, which is the point of a ceremony.
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setTyped('');
    }
  }, [open]);

  const readiness = useQuery({
    queryKey: LOCK_READINESS_QUERY_KEY,
    queryFn: ({ signal }) => readLockReadiness(signal),
    select: decodeLockReadiness,
    // Only while the ceremony is open: these counts exist to be read at the moment of decision, and
    // fetching them for every visitor to the Security tab would be a request nobody reads.
    enabled: open,
    // Always refetched on open. The counts move as employees connect their work identity and update
    // their apps, and a stale number is a wrong number at the only moment it matters.
    //
    // ⚠ THIS DOES NOT CLEAR `data`, and believing it does is the trap. The hook lives outside
    // `Dialog.Content`, so the observer survives a close: on reopen the PREVIOUS opening's counts
    // are still in `data`, `isPending` is false, and only `isFetching` distinguishes "these numbers
    // are current" from "these numbers are what we showed last time". Hence the `isFetching` term in
    // the gate below and the loading panel it drives.
    staleTime: 0,
    gcTime: 0,
  });

  const counts = readiness.data;
  // Gate 1 in full: the numbers arrived (not merely a 200 — see `countsComplete`), they are for THIS
  // opening (not a refetch still in flight), and they are complete.
  const countsReady = counts !== undefined && countsComplete(counts) && !readiness.isFetching;
  const gatesHold =
    countsReady && acknowledged && confirmationMatches(typed, companyName) && !submitting;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={SSO_COPY_DRAFT.lockTitle}
        className="flex flex-col gap-12"
        width={560}
        testId={LOCK_CEREMONY_TEST_ID}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {SSO_COPY_DRAFT.lockTitle}
        </Dialog.Title>

        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {SSO_COPY_DRAFT.lockIntro}
        </Dialog.Description>

        <ReadinessPanel query={readiness} />

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
          {SSO_COPY_DRAFT.lockBreakGlassNote}
        </p>

        {/* The escape hatch, with its price. Not reassurance — the sentence that says an employee
            can always get out also says what getting out costs them. */}
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
          {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-escape-hatch`)}
        >
          {SSO_COPY_DRAFT.lockEscapeHatchNote}
        </p>

        <label className="flex items-start gap-8">
          <Checkbox
            appearance="saas"
            checked={acknowledged}
            onCheckedChange={setAcknowledged}
            disabled={submitting}
            testId={`${LOCK_CEREMONY_TEST_ID}-acknowledge`}
          />
          <span className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
            {SSO_COPY_DRAFT.lockAcknowledge}
          </span>
        </label>

        <Field
          label={SSO_COPY_DRAFT.lockConfirmLabel}
          helper={`${SSO_COPY_DRAFT.lockConfirmPromptPrefix} — ${companyName} — ${SSO_COPY_DRAFT.lockConfirmPromptSuffix}`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          disabled={submitting}
          autoComplete="off"
          spellCheck={false}
          testId={`${LOCK_CEREMONY_TEST_ID}-confirm-input`}
        />

        {errorMessage != null && errorMessage.length > 0 && (
          <p
            className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
            {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-error`)}
          >
            {errorMessage}
          </p>
        )}

        <div className="flex items-center justify-end gap-12">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            testId={`${LOCK_CEREMONY_TEST_ID}-cancel`}
          >
            {SSO_COPY_DRAFT.lockCancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={!gatesHold}
            loading={submitting}
            onClick={onConfirm}
            testId={`${LOCK_CEREMONY_TEST_ID}-confirm`}
          >
            {SSO_COPY_DRAFT.lockConfirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function ReadinessPanel({
  query,
}: {
  query: ReturnType<typeof useQuery<unknown, Error, LockReadiness>>;
}) {
  // `isFetching` covers the reopen refetch as well as the first load: while it is true the numbers
  // on screen would be the previous opening's, and the gate refuses them, so the panel says so
  // rather than showing stale figures beside a disabled button with no explanation.
  if (query.isPending || query.isFetching) {
    return (
      <div
        className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
        {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-readiness-loading`)}
      >
        <p className="font-sans text-s13 text-text-secondary">
          {SSO_COPY_DRAFT.lockReadinessPending}
        </p>
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-12 w-1/2" />
      </div>
    );
  }

  if (query.isError) {
    // Gate 1, failing closed. There is no Confirm to enable from here — the caller keeps it
    // disabled while `query.data` is undefined — so this offers the only thing that can change that.
    return (
      <div
        className="flex flex-col items-start gap-8 rounded-lg border border-danger-100 bg-danger-50 p-16"
        {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-readiness-error`)}
      >
        <p className="font-sans text-s13 font-w500 text-danger-700">
          {isGivmoError(query.error) ? query.error.message : SSO_COPY_DRAFT.lockReadinessFailed}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
          {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-readiness-retry`)}
        >
          {SSO_COPY_DRAFT.lockReadinessRetry}
        </button>
      </div>
    );
  }

  const counts = query.data;

  // A 200 that carried no usable numbers is not a readiness report. It reaches here as a perfectly
  // defined all-`null` object, so without this branch the panel would render its heading and no rows
  // — an empty box beside a Confirm the caller has (correctly) disabled, with nothing saying why.
  // Same panel, same retry, as a rejected read: from the Owner's side the two are the same problem.
  if (!countsComplete(counts)) {
    return (
      <div
        className="flex flex-col items-start gap-8 rounded-lg border border-danger-100 bg-danger-50 p-16"
        {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-readiness-error`)}
      >
        <p className="font-sans text-s13 font-w500 text-danger-700">
          {SSO_COPY_DRAFT.lockReadinessFailed}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
          {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-readiness-retry`)}
        >
          {SSO_COPY_DRAFT.lockReadinessRetry}
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
      {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-readiness`)}
    >
      <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
        {SSO_COPY_DRAFT.lockReadinessTitle}
      </p>

      <CountRow
        label={SSO_COPY_DRAFT.lockLinkedEmployees}
        value={counts.linkedEmployees}
        testId={`${LOCK_CEREMONY_TEST_ID}-linked`}
      />
      <CountRow
        label={SSO_COPY_DRAFT.lockWouldBeLocked}
        value={counts.wouldBeLocked}
        testId={`${LOCK_CEREMONY_TEST_ID}-would-be-locked`}
      />
      <CountRow
        label={SSO_COPY_DRAFT.lockExemptAdmins}
        value={counts.exemptAdmins}
        testId={`${LOCK_CEREMONY_TEST_ID}-exempt-admins`}
      />
      <CountRow
        label={SSO_COPY_DRAFT.lockWithoutSsoIdentity}
        value={counts.withoutSsoIdentity}
        testId={`${LOCK_CEREMONY_TEST_ID}-without-identity`}
      />
      <CountRow
        label={SSO_COPY_DRAFT.lockBelowClientFloor}
        value={counts.belowClientFloor}
        testId={`${LOCK_CEREMONY_TEST_ID}-below-floor`}
      />
      <CountRow
        label={SSO_COPY_DRAFT.lockClientVersionUnknown}
        value={counts.clientVersionUnknown}
        testId={`${LOCK_CEREMONY_TEST_ID}-version-unknown`}
      />

      {clientFloorWasChecked(counts) ? (
        <p
          className="font-sans text-s12 text-text-tertiary"
          {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-floor`)}
        >
          {`${SSO_COPY_DRAFT.lockMinClientVersionLabel}: ${counts.minClientVersion}`}
        </p>
      ) : (
        <p
          className="font-sans text-s12 text-text-tertiary"
          {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-no-floor`)}
        >
          {SSO_COPY_DRAFT.lockNoFloorNote}
        </p>
      )}

      {/* Neutral tertiary text, deliberately NOT the warning tone this used to carry: a zero here
          means the exemption applies to none of the linked employees, not that nobody can turn the
          tier off. Styling it as an alarm would restate the false claim the words no longer make. */}
      {hasNoExemptAdmin(counts) && (
        <p
          className="font-sans text-s12 font-w400 leading-h15 text-text-tertiary"
          {...testIdProp(`${LOCK_CEREMONY_TEST_ID}-no-exempt-admin`)}
        >
          {SSO_COPY_DRAFT.lockNoExemptAdminWarning}
        </p>
      )}
    </div>
  );
}

/**
 * One count. A `null` value renders NOTHING — see the file header on manufactured zeroes.
 *
 * Unreachable through the panel today (`countsComplete` refuses the whole render before a `null`
 * gets here) and kept anyway: it is one line, and it is what stands between a future loosening of
 * that gate and a `null` reaching the DOM as blank or `NaN` beside a label an Owner will read as a
 * number.
 */
function CountRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: number | null;
  testId: string;
}) {
  if (value === null) return null;
  return (
    <div className="flex items-baseline justify-between gap-12">
      {/* `pretty`, so a label never strands its last word ("…an app version / from"). Not
          `balance`, the card titles' tool: on a three-line label it moves "— after the switch" to the
          START of a line (D-AS-38 (1)). */}
      <span className="font-sans text-s13 text-text-secondary text-pretty">{label}</span>
      <span
        className="font-sans text-s15 font-w600 tabular-nums text-text-strong"
        {...testIdProp(testId)}
      >
        {value}
      </span>
    </div>
  );
}
